import { defineStore } from 'pinia'
import { ref } from 'vue'
import { notificationApi, type AppNotification } from '../services/notificationApi'
import { registerIdentityReset } from './auth'
import { pollWhileVisible } from '../utils/visiblePoll'

export const useNotificationStore = defineStore('notifications', () => {
  const items = ref<AppNotification[]>([])
  const unreadCount = ref(0)
  /** All notifications, or only the unread ones (which reaches older unread). */
  const unreadOnly = ref(false)
  /** How many the server holds for the current view; the list shows the newest 30. */
  const total = ref(0)
  /** The last read failed: the bell says so instead of "Nothing yet." (audit shell #7). */
  const itemsError = ref(false)
  let stopTimer: (() => void) | null = null

  // `/v1/notifications` is per user (RV-FE N1). Every change of who is signed
  // in drops the list and the count, and `generation` drops an answer to a read
  // the previous user started.
  let generation = 0
  function resetForIdentityChange(): void {
    generation += 1
    items.value = []
    unreadCount.value = 0
    total.value = 0
    itemsError.value = false
    unreadOnly.value = false
  }
  registerIdentityReset(resetForIdentityChange)

  async function fetchUnreadCount() {
    const gen = generation
    try {
      const count = await notificationApi.unreadCount()
      if (gen === generation) unreadCount.value = count
    } catch { /* silent: the badge keeps its last count for this user */ }
  }
  async function fetchItems() {
    const gen = generation
    try {
      const res = await notificationApi.list(unreadOnly.value)
      if (gen !== generation) return
      items.value = res.items
      total.value = res.total
      itemsError.value = false
    } catch {
      // A failed read shows nothing rather than a list it could not confirm.
      if (gen === generation) {
        items.value = []
        total.value = 0
        itemsError.value = true
      }
    }
  }
  async function markRead(id: string) {
    await notificationApi.markRead(id)
    const n = items.value.find((i) => i.id === id)
    if (n && !n.read_at) { n.read_at = new Date().toISOString(); unreadCount.value = Math.max(0, unreadCount.value - 1) }
  }
  // Marks only the unread notifications on screen. It used to clear every
  // unread one, including older ones the 30-row list never showed (audit
  // shell #2); the badge is then re-read rather than zeroed.
  async function markAllRead() {
    const shown = items.value.filter((i) => !i.read_at).map((i) => i.id)
    if (shown.length === 0) return
    await notificationApi.markAllRead(shown)
    const now = new Date().toISOString()
    items.value.forEach((i) => { if (!i.read_at) i.read_at = now })
    await fetchUnreadCount()
  }
  // Paused while the tab is hidden; refreshed at once when it is shown again.
  function startPolling() {
    fetchUnreadCount()
    if (!stopTimer) stopTimer = pollWhileVisible(fetchUnreadCount, 45000)
  }
  function stopPolling() { if (stopTimer) { stopTimer(); stopTimer = null } }

  return { items, unreadCount, unreadOnly, total, itemsError, fetchUnreadCount, fetchItems, markRead, markAllRead, startPolling, stopPolling }
})
